import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import BaseModal from '../../../components/Portal/BaseModal';
import CourseGroupFormModal from '../CourseGroupForm/CourseGroupFormModal';
import TeacherAssignmentModal from '../../../components/Portal/TeacherAssignmentModal';
import { AdminNotesButton } from './AdminNotes';
import {
  ModalHeader, CloseButton, ModalBody, ModalFooter,
  ScopeChoice, ScopeChoiceButton,
  Field, FieldRow, InfoGrid, InfoItem, Pill,
  PrimaryButton, SecondaryButton, DangerButton, ErrorState
} from './Timetable.styles';
import {
  LESSON_STATUS_OPTIONS, LESSON_TYPE_OPTIONS,
  getLessonStatusColor, getLessonStatusLabel,
  formatTeachers, formatEnrolment, studentNames,
  cancelOccurrence, markNoShow, restoreOccurrence, applyToOccurrence,
  updateSchedule, updateSlot, isRecurring, isSpecialSlot
} from '../../../api/timetableApi';
import {
  deleteCourseGroup, getCourseGroup, updateCourseGroup, isSpecialTerm
} from '../../../api/courseGroupPortalApi';

/**
 * Detail + editing for one class.
 *
 * Editing offers the same "this week / whole term" choice the drag gesture does, so a single
 * lesson of a course group can be nudged without leaving the timetable:
 *
 * - **This week only** writes a LessonPortal override. The course group's recurrence is never
 *   touched, so the rest of the term — and billing, credits and materials that read it — is
 *   unaffected.
 * - **Whole term** calls `updateSchedule`, which rewrites just the one matching entry of
 *   `CourseGroupPortal.schedules[]`. Only the repeating facts (day, time, room) can travel
 *   this way; status and notes belong to a single occurrence and are disabled in that mode.
 *
 * `canDeleteGroup` additionally offers deleting the whole course group this lesson belongs
 * to — manager-and-above only, gated by the caller. This is the one action here that is not
 * scoped to a single occurrence: it removes the course group, every enrolled student and
 * every remaining class, so it lives behind a spelled-out confirm.
 */

const LessonModal = ({
  occurrence, canEdit, showClassLink = false, canEditGroup = false,
  canDeleteGroup = false, adminNotesEnabled = false, onClose, onSaved
}) => {
  const navigate = useNavigate();
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  // The course group behind this class, once it has been asked for. Held here rather than
  // loaded up front because most people open a lesson to read it, not to change the term.
  const [groupToEdit, setGroupToEdit] = useState(null);
  // Same idea, for the Teachers modal — kept separate from groupToEdit since the two dialogs
  // are never open at once and read different shapes of "done".
  const [teacherGroup, setTeacherGroup] = useState(null);
  // Local because `occurrence` is a snapshot from when the dialog opened.
  const [adminNotes, setAdminNotes] = useState(occurrence.adminNotes || '');
  const handleAdminNotesSaved = (saved) => {
    setAdminNotes(saved);
    onSaved();
  };
  const [form, setForm] = useState({
    date: occurrence.date,
    startTime: occurrence.startTime,
    endTime: occurrence.endTime,
    room: occurrence.room || '',
    status: occurrence.status,
    notes: occurrence.notes || ''
  });

  const isOverridden = Boolean(occurrence.lessonId);
  const roll = studentNames(occurrence);

  // A special-term class is its slot. Editing it has to move the slot itself, or the Course
  // Groups page — and billing, credits and materials with it — keeps reading the date the
  // class no longer runs on. Only true while nothing has overridden the slot already; an
  // existing override is deliberately in charge of that date.
  const editsSlot = isSpecialSlot(occurrence) && !isOverridden;

  // A one-off lesson (trial, assessment, ad-hoc) has no course group behind it, so there is
  // nothing to delete at that level.
  const isCourseGroup = Boolean(occurrence.courseGroupId);

  /**
   * Whether "whole term" is even a meaningful choice here.
   *
   * It is, for any occurrence of a recurring class — including one that already carries an
   * override row. Having changed a single week must not remove the ability to then say "move
   * the whole term", which is exactly the case where the two answers differ most. A standalone
   * lesson and a special-term slot have no weekly schedule behind them and are the real
   * exclusions. Same rule the drag gesture uses.
   */
  const canScope = isRecurring(occurrence);
  const [scope, setScope] = useState('week');
  const wholeTerm = canScope && scope === 'term';

  const setField = (key) => (event) =>
    setForm(prev => ({ ...prev, [key]: event.target.value }));

  const run = async (action) => {
    setSaving(true);
    setError('');
    try {
      await action();
      await onSaved();
      onClose();
    } catch (e) {
      setError(e.message || 'Something went wrong');
      setSaving(false);
    }
  };

  /**
   * Open the course group behind this class, here on the timetable.
   *
   * This replaced a jump to My Classes, which only ever worked for the person teaching the
   * class: My Classes lists what you teach, so a manager following the link to somebody else's
   * lesson arrived at a page with nothing selected. Editing it in place also means the change
   * lands on the course group itself, so the timetable and the Course Groups page say the same
   * thing without anyone having to go and repeat the edit.
   */
  const openCourseGroup = async () => {
    setSaving(true);
    setError('');
    try {
      setGroupToEdit(await getCourseGroup(occurrence.courseGroupId));
    } catch (e) {
      setError(e.message || 'Could not open the course group');
    } finally {
      setSaving(false);
    }
  };

  const openTeacherAssignment = async () => {
    setSaving(true);
    setError('');
    try {
      setTeacherGroup(await getCourseGroup(occurrence.courseGroupId));
    } catch (e) {
      setError(e.message || 'Could not open teacher assignment');
    } finally {
      setSaving(false);
    }
  };

  /**
   * Mirrors the Teachers modal on the Course Groups page: adding a teacher auto-assigns them
   * to any schedule/slot of this course group that needs every one of their subjects and has
   * no teacher yet, so a class picked up here shows the new teacher on the grid without a
   * second trip to Course Groups.
   */
  const handleTeacherAdded = async ({ teacherId, subjectIds }) => {
    if (!teacherGroup || !subjectIds?.length) return;
    const isSpecial = isSpecialTerm(teacherGroup.term);
    let hasChanges = false;

    try {
      if (isSpecial && teacherGroup.slots?.length) {
        const updatedSlots = teacherGroup.slots.map(slot => {
          if (slot.teacherId || !slot.subjectId || !subjectIds.includes(slot.subjectId)) {
            return slot;
          }
          hasChanges = true;
          return { ...slot, teacherId };
        });
        if (hasChanges) await updateCourseGroup(teacherGroup.id, { slots: updatedSlots });
      } else if (!isSpecial && teacherGroup.schedules?.length) {
        const updatedSchedules = teacherGroup.schedules.map(schedule => {
          const bookingSubjects = (schedule.subjectIds?.length
            ? schedule.subjectIds
            : [schedule.subjectId]).filter(Boolean);
          if (schedule.teacherId || !bookingSubjects.length
              || !bookingSubjects.every(sid => subjectIds.includes(sid))) {
            return schedule;
          }
          hasChanges = true;
          return { ...schedule, teacherId };
        });
        if (hasChanges) await updateCourseGroup(teacherGroup.id, { schedules: updatedSchedules });
      }

      if (hasChanges) {
        setTeacherGroup(await getCourseGroup(teacherGroup.id));
        await onSaved();
      }
    } catch (e) {
      // The teacher was added successfully either way; only the auto-assign step failed.
      console.error('Error auto-assigning teacher to slots:', e);
    }
  };

  // Keeps the Teachers modal (schedules[].reliefAssignments) and the timetable grid in sync
  // after a relief assignment is set or cleared.
  const handleReliefChange = async () => {
    if (!teacherGroup) return;
    try {
      setTeacherGroup(await getCourseGroup(teacherGroup.id));
      await onSaved();
    } catch (e) {
      console.error('Error refreshing after relief change:', e);
    }
  };

  const handleDeleteGroup = () => {
    const name = occurrence.title || 'this course group';
    const confirmed = window.confirm(
      `Delete ${name}?\n\n`
      + 'This removes the whole course group, unenrols every student in it, and takes every '
      + 'remaining class off the timetable. It cannot be undone.'
    );
    if (!confirmed) return;
    run(() => deleteCourseGroup(occurrence.courseGroupId));
  };

  const handleSave = () => run(async () => {
    if (form.endTime <= form.startTime) {
      throw new Error('End time must be after start time');
    }

    if (editsSlot) {
      // Status and notes have nowhere to live on a slot, so they still become an override.
      // Written first, against the date the slot is still on: it then reads as a plain tag on
      // that class, and the slot move below carries it across to the new date.
      const statusChanged = form.status !== occurrence.status;
      const notesChanged = (form.notes || '') !== (occurrence.notes || '');
      if (statusChanged || notesChanged) {
        await applyToOccurrence(occurrence, { status: form.status, notes: form.notes });
      }
      await updateSlot(occurrence, {
        date: form.date,
        startTime: form.startTime,
        endTime: form.endTime,
        room: form.room
      });
      return;
    }

    if (!wholeTerm) {
      // One write covers every edited field, whether this creates the override row or
      // updates one that already exists.
      await applyToOccurrence(occurrence, form);
      return;
    }

    // Whole term rewrites the course group's recurrence rule, which only carries the
    // repeating facts. Status and notes describe one occurrence and have nowhere to live on
    // a schedule, so the form disables them in this mode rather than dropping them silently.
    const changes = {
      startTime: form.startTime,
      endTime: form.endTime,
      room: form.room
    };
    // Moving the date in whole-term mode means "this class runs on that weekday from now on".
    if (form.date !== occurrence.date) {
      changes.dayOfWeek = ((new Date(`${form.date}T00:00:00`).getDay() + 6) % 7) + 1;
    }
    await updateSchedule(occurrence, changes);
  });

  // The course group form takes over while it is open: the two dialogs describe the same
  // class at different scopes, and stacking them invites editing one behind the other.
  if (groupToEdit) {
    return (
      <CourseGroupFormModal
        mode="edit"
        group={groupToEdit}
        onSaved={onSaved}
        onAdminNotesSaved={handleAdminNotesSaved}
        onClose={() => setGroupToEdit(null)}
      />
    );
  }

  // Same idea for the Teachers modal — it takes over the screen rather than stacking on top.
  if (teacherGroup) {
    return (
      <TeacherAssignmentModal
        show
        onClose={() => setTeacherGroup(null)}
        courseGroup={teacherGroup}
        onUpdate={handleTeacherAdded}
        onReliefChange={handleReliefChange}
      />
    );
  }

  return (
    <BaseModal maxWidth="620px">
      <ModalHeader>
        <h2>{occurrence.title || '(untitled class)'}</h2>
        <CloseButton onClick={onClose} aria-label="Close">×</CloseButton>
      </ModalHeader>

      <ModalBody>
        {error && <ErrorState>{error}</ErrorState>}

        {!editing ? (
          <>
            <InfoGrid>
              <InfoItem>
                <label>Date</label>
                <div>{occurrence.date}</div>
              </InfoItem>
              <InfoItem>
                <label>Time</label>
                <div>{occurrence.startTime} – {occurrence.endTime}</div>
              </InfoItem>
              <InfoItem>
                <label>Room</label>
                <div>{occurrence.room || '—'}</div>
              </InfoItem>
              <InfoItem>
                <label>Teacher</label>
                <div>{formatTeachers(occurrence) || '—'}</div>
              </InfoItem>
              <InfoItem>
                <label>Campus</label>
                <div>{occurrence.campusName || '—'}</div>
              </InfoItem>
              <InfoItem>
                <label>Year / Type</label>
                <div>
                  {occurrence.year ? `Year ${occurrence.year}` : '—'}
                  {occurrence.typeName ? ` · ${occurrence.typeName}` : ''}
                </div>
              </InfoItem>
              <InfoItem>
                <label>Students</label>
                <div>{formatEnrolment(occurrence)}</div>
              </InfoItem>
              <InfoItem>
                <label>Status</label>
                <div>
                  <Pill $color={getLessonStatusColor(occurrence.status)}>
                    {getLessonStatusLabel(occurrence.status)}
                  </Pill>
                </div>
              </InfoItem>
            </InfoGrid>

            {/* The full roll, never abbreviated. The card on the grid shortens a long list
                to fit; this is the place that has room to answer "who exactly is in this
                class", which is what a teacher opens the lesson for. */}
            <InfoItem style={{ marginBottom: 14 }}>
              <label>Enrolled{roll.length > 0 ? ` (${roll.length})` : ''}</label>
              <div>{roll.length > 0 ? roll.join(', ') : 'Nobody enrolled yet'}</div>
            </InfoItem>

            {occurrence.guestName && (
              <InfoItem style={{ marginBottom: 14 }}>
                <label>Guest</label>
                <div>{occurrence.guestName}</div>
              </InfoItem>
            )}

            {occurrence.notes && (
              <InfoItem>
                <label>Notes</label>
                <div>{occurrence.notes}</div>
              </InfoItem>
            )}

            {isOverridden && (
              <InfoItem style={{ marginTop: 14 }}>
                <label>This occurrence</label>
                <div style={{ color: '#6b7280', fontSize: 12 }}>
                  Changed for this date only — the rest of the term is unaffected.
                </div>
              </InfoItem>
            )}
          </>
        ) : (
          <>
            {canScope && (
              <ScopeChoice>
                <ScopeChoiceButton
                  type="button"
                  $active={scope === 'week'}
                  onClick={() => setScope('week')}
                >
                  <strong>This week only</strong>
                  <span>Just {occurrence.date}. The rest of the term stays as it is.</span>
                </ScopeChoiceButton>
                <ScopeChoiceButton
                  type="button"
                  $active={scope === 'term'}
                  onClick={() => setScope('term')}
                >
                  <strong>Whole term</strong>
                  <span>
                    Every remaining week of this class.
                    {isOverridden ? ` This week’s separate change is cleared.` : ''}
                  </span>
                </ScopeChoiceButton>
              </ScopeChoice>
            )}

            <FieldRow>
              <Field>
                <label>{wholeTerm ? 'Day (moves the whole term)' : 'Date'}</label>
                <input type="date" value={form.date} onChange={setField('date')} />
              </Field>
              <Field>
                <label>Room</label>
                <input
                  type="text"
                  value={form.room}
                  onChange={setField('room')}
                  placeholder="e.g. Room 3"
                />
              </Field>
            </FieldRow>

            <FieldRow>
              <Field>
                <label>Start time</label>
                <input type="time" value={form.startTime} onChange={setField('startTime')} />
              </Field>
              <Field>
                <label>End time</label>
                <input type="time" value={form.endTime} onChange={setField('endTime')} />
              </Field>
            </FieldRow>

            <Field>
              <label>Status</label>
              <select value={form.status} onChange={setField('status')} disabled={wholeTerm}>
                {LESSON_STATUS_OPTIONS.map(option => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </Field>

            <Field>
              <label>Notes</label>
              <textarea
                value={form.notes}
                onChange={setField('notes')}
                placeholder="Admin note for this lesson"
                disabled={wholeTerm}
              />
            </Field>

            <div style={{ fontSize: 12, color: '#6b7280' }}>
              {wholeTerm
                ? `Time, room and day apply to every remaining week. Status and notes describe a single lesson, so they are only editable in "this week only".${
                    isOverridden ? ' The change made to this week on its own is discarded.' : ''}`
                : editsSlot
                  ? 'This class is dated individually, so the change moves the class itself. The Course Groups page will show the new date.'
                  : canScope
                    ? `Changes apply to ${occurrence.date} only.`
                    : 'Changes apply to this date only. To change the whole term, edit the course group.'}
            </div>
          </>
        )}
      </ModalBody>

      <ModalFooter>
        {canEdit && !editing && (
          <>
            {canDeleteGroup && isCourseGroup && (
              <DangerButton
                onClick={handleDeleteGroup}
                disabled={saving}
                style={{ marginRight: 'auto' }}
              >
                Delete course group
              </DangerButton>
            )}
            {isOverridden && (
              <DangerButton
                onClick={() => run(() => restoreOccurrence(occurrence))}
                disabled={saving}
              >
                Undo changes
              </DangerButton>
            )}
            {occurrence.status !== 'cancelled' && (
              <SecondaryButton
                onClick={() => run(() => cancelOccurrence(occurrence))}
                disabled={saving}
              >
                Cancel class
              </SecondaryButton>
            )}
            <SecondaryButton
              onClick={() => run(() => markNoShow(occurrence, 'student'))}
              disabled={saving}
            >
              No-show
            </SecondaryButton>
            <PrimaryButton onClick={() => setEditing(true)} disabled={saving}>
              Edit
            </PrimaryButton>
          </>
        )}

        {editing && (
          <>
            <SecondaryButton onClick={() => setEditing(false)} disabled={saving}>
              Cancel
            </SecondaryButton>
            <PrimaryButton onClick={handleSave} disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </PrimaryButton>
          </>
        )}

        {!editing && canEditGroup && isCourseGroup && adminNotesEnabled && (
          <AdminNotesButton
            courseGroupId={occurrence.courseGroupId}
            title={occurrence.title}
            note={adminNotes}
            onSaved={handleAdminNotesSaved}
            Button={SecondaryButton}
            buttonProps={{ disabled: saving }}
          />
        )}

        {/* The term behind this one class: its schedule, subjects, holiday weeks. Opened here
            rather than on another screen, so "every Monday" and "this Monday" are edited in
            the same place and the course group is what both of them write to. */}
        {!editing && canEditGroup && isCourseGroup && (
          <SecondaryButton onClick={openCourseGroup} disabled={saving}>
            Edit course group
          </SecondaryButton>
        )}

        {/* Same gate and the same "fetch the group, hand it to the same modal Course Groups
            uses" approach as Edit course group above — just opens the Teachers modal instead
            of the full form. */}
        {!editing && canEditGroup && isCourseGroup && (
          <SecondaryButton onClick={openTeacherAssignment} disabled={saving}>
            Teacher
          </SecondaryButton>
        )}

        {/* Straight from "what am I teaching at 5?" to that class's materials and students.
            Teachers reach the timetable to plan their day, and the next thing they want is
            almost always the class itself. Only offered to whoever teaches it: My Classes
            lists what you teach, so for anybody else the link opens an empty page. */}
        {!editing && showClassLink && occurrence.courseGroupId && (
          <PrimaryButton
            onClick={() => navigate(`/portal/my-course-groups?group=${occurrence.courseGroupId}`)}
          >
            Open in My Classes
          </PrimaryButton>
        )}

        {!editing && <SecondaryButton onClick={onClose}>Close</SecondaryButton>}
      </ModalFooter>
    </BaseModal>
  );
};

export const LESSON_TYPES_FOR_FORM = LESSON_TYPE_OPTIONS;

export default LessonModal;
